/**
 * Yvine Design — section runtime. Every section asset registers an init with
 * Art.section(name, init); the init runs once for every element carrying
 * data-art="name" — on first paint, after each in-place page swap
 * (art:page:swap from art-transitions.js) and on theme-editor section loads.
 * Section scripts therefore live in cached assets instead of inline blocks,
 * and any Liquid values they need come from data-* attributes on that element.
 */
window.Art = (function () {
  var inits = {};
  var done = new WeakSet();

  function boot(scope) {
    (scope || document).querySelectorAll('[data-art]').forEach(function (el) {
      var init = inits[el.getAttribute('data-art')];
      if (!init || done.has(el)) return;
      done.add(el);
      try { init(el); } catch (err) { if (window.console) console.error('art:' + el.getAttribute('data-art'), err); }
    });
  }

  function section(name, init) {
    inits[name] = init;
    boot();
  }

  function ready(el) { return done.has(el); }

  document.addEventListener('art:page:swap', function () { boot(); });
  document.addEventListener('shopify:section:load', function () { boot(); });

  // Scroll to the top of the page (or to an element) instantly. html and the
  // desktop .page-wrapper scroll container both have scroll-behavior:smooth,
  // which would otherwise animate the jump; on desktop the window itself never
  // scrolls, so both are moved.
  function jumpTo(target) {
    var wrapper = document.querySelector('.page-wrapper');
    var top = { top: 0, left: 0, behavior: 'instant' };
    try {
      window.scrollTo(top);
      if (wrapper) wrapper.scrollTo(top);
      if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
    } catch (err) {
      window.scrollTo(0, 0);
      if (wrapper) wrapper.scrollTop = 0;
      if (target) target.scrollIntoView();
    }
  }

  /* One fade for every in-page change — filters, pills, sort, pagination —
     with the page transition's own timing (the --art-tx-* tokens in
     art-layout.css): the content fades out, changes while invisible, and fades
     back in. Anything slow (a fetch) runs during the fade-out, never after it.

       Art.fade(els, apply, { fetch, toTop })
         els    element or list of elements that fade
         apply  function(result) — the DOM change, made while invisible
         fetch  optional function returning a promise; its result goes to apply
         toTop  true = jump to the top of the page while invisible

     A newer fade on the same element supersedes an older one: the older
     apply is skipped, so rapid clicks settle on the last choice. */
  var OUT_MS = 220;
  var fadeSeq = new WeakMap();
  function reduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function fade(els, apply, opts) {
    opts = opts || {};
    var list = !els ? [] : (els.nodeType ? [els] : Array.prototype.slice.call(els)).filter(Boolean);
    var key = list[0] || document.documentElement;
    var my = (fadeSeq.get(key) || 0) + 1;
    fadeSeq.set(key, my);
    list.forEach(function (el) { el.classList.remove('art-fade-in'); el.classList.add('art-fade-out'); });
    var out = new Promise(function (res) { setTimeout(res, reduced() ? 0 : OUT_MS); });
    var work = opts.fetch ? opts.fetch() : null;
    return Promise.all([out, work]).then(function (r) {
      if (fadeSeq.get(key) !== my) return;
      if (apply) apply(r[1]);
      if (opts.toTop) jumpTo();
      list.forEach(function (el) {
        el.classList.remove('art-fade-out');
        void el.offsetWidth; // restart the keyframes even if the class was just there
        el.classList.add('art-fade-in');
        el.addEventListener('animationend', function done(e) {
          if (e.target !== el) return;
          el.classList.remove('art-fade-in');
          el.removeEventListener('animationend', done);
        });
      });
    }, function (err) {
      if (fadeSeq.get(key) === my) list.forEach(function (el) { el.classList.remove('art-fade-out'); });
      throw err;
    });
  }

  /* The on-screen keyboard is not a layout change.
     Opening it resizes the viewport (a lot in Android in-app browsers, a
     little on iOS while Safari pans to the field), and every script that
     re-measures on resize then moves the backdrop, the scenery windows and the
     gallery bands under the visitor's thumb — the "flick". A resize whose
     width is unchanged while a text field has focus is the keyboard: callers
     skip it, and one synthetic resize after the field loses focus lets them
     catch up once the keyboard has gone. */
  var lastWidth = window.innerWidth;
  var skippedResize = false;
  function typing() {
    var a = document.activeElement;
    if (!a || a === document.body) return false;
    if (a.isContentEditable || a.tagName === 'TEXTAREA') return true;
    if (a.tagName !== 'INPUT') return false;
    return !/^(checkbox|radio|button|submit|reset|range|color|file|image|hidden)$/i.test(a.type || 'text');
  }
  function keyboardResize() {
    var w = window.innerWidth;
    if (w !== lastWidth) { lastWidth = w; return false; }
    if (typing()) { skippedResize = true; return true; }
    return false;
  }
  document.addEventListener('focusout', function () {
    if (!skippedResize) return;
    setTimeout(function () {
      if (typing()) return; // focus moved to another field: keyboard still up
      skippedResize = false;
      window.dispatchEvent(new Event('resize'));
    }, 350);
  });

  /* Filtering a list item by item (the blog's topics): what no longer matches
     fades away, what stays glides to its new place, what now matches fades
     in, and the list's own height eases to its new size so whatever sits
     below it (the footer) drifts into place instead of jumping. A touch
     slower and softer than a page change (--art-item-* in art-layout.css).

       Art.filterItems(items, matches, { container, change, groups, groupMatches, done })
         items         the elements that can show or hide (hidden attribute)
         matches       function(el) -> true to show it
         glide         'transform' (default) moves the items themselves; 'clone'
                       flies a copy of each in an overlay while the real one
                       waits invisible in its new place — for items inside CSS
                       columns, where Safari stops painting every column but
                       the first while a child is transformed or translucent
         container     optional element whose height eases between the two states
         change        optional function run while the leavers are gone and
                       before anything glides: a layout switch, a jump to top
         groups        optional containers that fade with their items but do
                       not glide themselves (the Gallery's bands) —
         groupMatches  function(group) -> true to show it
         done          optional function called once the list is settled

     The glide is FLIP: every item that stays is measured before and after the
     change, drawn back where it was with a transform and let go. A newer call
     supersedes one still running. */
  var filterSeq = 0;
  // Size styles are borrowed, never assumed free: a band lays its pieces out
  // with inline left/top/width/height (art-strip.js), so the glide puts back
  // exactly what was there and touches nothing it did not set.
  var sized = [];
  function unsize() {
    sized.forEach(function (s) {
      s.el.style.width = s.w;
      s.el.style.height = s.h;
      s.el.style.overflow = s.o;
    });
    sized = [];
  }
  var flyLayer = null;
  function clearFly() {
    if (flyLayer) { flyLayer.remove(); flyLayer = null; }
    document.querySelectorAll('[data-art-flying]').forEach(function (el) {
      el.style.visibility = '';
      el.removeAttribute('data-art-flying');
    });
  }
  // The copies fly in a fixed layer on <body> (outside .page-wrapper, whose
  // backdrop-filter would trap anything fixed), clipped to the area under the
  // header so nothing crosses over it.
  function fly(items, first, ms) {
    clearFly();
    var head = document.getElementById('header-group');
    var frame = document.querySelector('.page-wrapper');
    var f = frame ? frame.getBoundingClientRect() : { left: 0, right: window.innerWidth, bottom: window.innerHeight };
    var top = head ? Math.max(0, head.getBoundingClientRect().bottom) : 0;
    var clip = [top, Math.max(0, window.innerWidth - f.right), Math.max(0, window.innerHeight - f.bottom), Math.max(0, f.left)];
    flyLayer = document.createElement('div');
    flyLayer.setAttribute('aria-hidden', 'true');
    flyLayer.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:45;clip-path:inset(' + clip.join('px ') + 'px)';
    items.forEach(function (el, i) {
      var a = first[i], b = el.getBoundingClientRect();
      if (!b.width || !b.height) return;
      // A piece that was off screen rises gently from just past the edge
      // instead of racing in from thousands of pixels away.
      var vh = window.innerHeight;
      if (a.top > vh) a = { left: a.left, top: vh + 24, width: a.width, height: a.height };
      else if (a.top + a.height < 0) a = { left: a.left, top: -a.height - 24, width: a.width, height: a.height };
      var copy = el.cloneNode(true);
      copy.removeAttribute('id');
      // Starts exactly where and as big as the piece was; moves and resizes
      // to its new box. The picture is cropped (cover), never stretched, while
      // its frame changes shape between the band and the grid.
      copy.style.cssText = 'position:absolute;margin:0;box-sizing:border-box;overflow:hidden;left:' + a.left + 'px;top:' + a.top + 'px;width:' + a.width + 'px;height:' + a.height + 'px';
      copy.querySelectorAll('img, video').forEach(function (m) { m.style.width = '100%'; m.style.height = '100%'; m.style.objectFit = 'cover'; });
      copy._to = b;
      flyLayer.appendChild(copy);
      el.style.visibility = 'hidden';
      el.setAttribute('data-art-flying', '');
    });
    document.body.appendChild(flyLayer);
    void flyLayer.offsetWidth;
    var ease = ms + 'ms var(--art-ease-soft)';
    Array.prototype.forEach.call(flyLayer.children, function (copy) {
      var b = copy._to;
      copy.style.transition = 'left ' + ease + ', top ' + ease + ', width ' + ease + ', height ' + ease;
      copy.style.left = b.left + 'px';
      copy.style.top = b.top + 'px';
      copy.style.width = b.width + 'px';
      copy.style.height = b.height + 'px';
    });
  }
  var ITEM_OUT_MS = 320, ITEM_IN_MS = 420; // = --art-item-out / --art-item-in
  function filterItems(items, matches, opts) {
    opts = opts || {};
    var my = ++filterSeq;
    clearFly();
    unsize();
    var cloneGlide = opts.glide === 'clone';
    var list = Array.prototype.slice.call(items);
    var leaving = [], entering = [], staying = [];
    var groups = opts.groups ? Array.prototype.slice.call(opts.groups) : [];
    var groupsOut = [], groupsIn = [];
    groups.forEach(function (g) {
      var show = !!opts.groupMatches(g);
      g.classList.remove('art-item-out', 'art-item-in');
      if (show && g.hidden) groupsIn.push(g);
      else if (!show && !g.hidden) groupsOut.push(g);
    });
    var changing = groupsOut.concat(groupsIn);
    function inChangingGroup(el) { return changing.some(function (g) { return g.contains(el); }); }
    var quiet = []; // items whose whole group fades: they only switch
    list.forEach(function (el) {
      var show = !!matches(el);
      el.style.transition = '';
      el.style.transform = '';
      el.classList.remove('art-item-out', 'art-item-in');
      var toggles = (show && el.hidden) || (!show && !el.hidden);
      if (toggles && inChangingGroup(el)) quiet.push([el, show]);
      else if (show && el.hidden) entering.push(el);
      else if (!show && !el.hidden) leaving.push(el);
      else if (show) staying.push(el);
    });
    if (reduced()) {
      quiet.forEach(function (q) { q[0].hidden = !q[1]; });
      leaving.concat(groupsOut).forEach(function (el) { el.hidden = true; });
      entering.concat(groupsIn).forEach(function (el) { el.hidden = false; });
      if (opts.change) opts.change();
      if (opts.done) opts.done();
      return;
    }
    var first = staying.map(function (el) { return el.getBoundingClientRect(); });
    var box = opts.container;
    if (box) { box.style.transition = ''; box.style.height = ''; box.style.overflowY = ''; }
    leaving.concat(groupsOut).forEach(function (el) { el.classList.add('art-item-out'); });
    setTimeout(function () {
      if (my !== filterSeq) return;
      var fromH = box ? box.offsetHeight : 0;
      leaving.concat(groupsOut).forEach(function (el) { el.hidden = true; el.classList.remove('art-item-out'); });
      quiet.forEach(function (q) { q[0].hidden = !q[1]; });
      entering.concat(groupsIn).forEach(function (el) { el.hidden = false; el.classList.add('art-item-in'); });
      if (opts.change) opts.change();
      if (cloneGlide) { fly(staying, first, ITEM_IN_MS); staying = []; }
      // Measure every end state first (positions, sizes, the list's height),
      // then draw each item back where and as big as it was, and let go: it
      // glides AND resizes — a card left alone in its row no longer snaps to
      // its own height (grid rows stretch cards to the tallest one).
      var last = staying.map(function (el) { return el.getBoundingClientRect(); });
      var toH = box ? box.offsetHeight : 0;
      var ease = ITEM_IN_MS + 'ms var(--art-ease-soft)';
      var moving = [];
      staying.forEach(function (el, i) {
        var a = first[i], b = last[i];
        var dx = a.left - b.left, dy = a.top - b.top;
        var resize = Math.abs(a.width - b.width) > 1 || Math.abs(a.height - b.height) > 1;
        if (!dx && !dy && !resize) return;
        el.style.transition = 'none';
        el.style.transform = 'translate(' + dx + 'px, ' + dy + 'px)';
        if (resize) {
          sized.push({ el: el, w: el.style.width, h: el.style.height, o: el.style.overflow });
          el.style.width = a.width + 'px';
          el.style.height = a.height + 'px';
          el.style.overflow = 'hidden';
        }
        moving.push([el, resize, b]);
      });
      void document.body.offsetWidth; // commit the "from" state
      moving.forEach(function (m) {
        var el = m[0];
        el.style.transition = m[1] ? 'transform ' + ease + ', width ' + ease + ', height ' + ease : 'transform ' + ease;
        el.style.transform = '';
        if (m[1]) { el.style.width = m[2].width + 'px'; el.style.height = m[2].height + 'px'; }
      });
      // The list eases from its old height to the new one; clipped meanwhile
      // so a longer list does not spill over the footer while it grows.
      if (box) {
        if (Math.abs(toH - fromH) > 1) {
          box.style.overflowY = 'clip';
          box.style.height = fromH + 'px';
          void box.offsetHeight;
          box.style.transition = 'height ' + ITEM_IN_MS + 'ms var(--art-ease-soft)';
          box.style.height = toH + 'px';
        }
      }
      if (opts.done) opts.done();
      setTimeout(function () {
        if (my !== filterSeq) return;
        if (box) { box.style.transition = ''; box.style.height = ''; box.style.overflowY = ''; }
        clearFly();
        unsize();
        list.forEach(function (el) {
          el.style.transition = '';
          el.classList.remove('art-item-in');
        });
        groups.forEach(function (g) { g.classList.remove('art-item-in'); });
      }, ITEM_IN_MS + 40);
    }, ITEM_OUT_MS);
  }

  return { section: section, boot: boot, ready: ready, fade: fade, jumpTo: jumpTo, typing: typing, keyboardResize: keyboardResize, filterItems: filterItems };
})();
